<template>
  <div>
    <div class="tw-flex tw-gap-2 tw-mb-8 tw-flex-wrap">
      <i-button
        v-if="$isInRole(['ADMIN', 'CONSULTANT', 'HR'])"
        class="tw-tracking-wider"
        @click="$emit('open-edit')"
      >
        {{ $t('foreigners.basic_info.edit_button') }}
      </i-button>

      <template v-if="$isInRole(['ADMIN', 'CONSULTANT'])">
        <i-confirm-popover
          v-if="foreigner.registered"
          :cancel-text="$t('common.actions.cancel')"
          :submit-text="$t('common.actions.reset_short')"
          @submit="resetPassword"
        >
          <template #reference>
            <i-button
              class="tw-tracking-wider"
              :is-loading="isPasswordResetting"
            >
              {{ $t('foreigners.basic_info.reset_button') }}
            </i-button>
          </template>
          <template #content>
            <div>
              {{ $t('foreigners.basic_info.reset_password') }}
              <span class="tw-whitespace-nowrap">
                {{ foreigner.first_name }} {{ foreigner.last_name }}?
              </span>
            </div>
          </template>
        </i-confirm-popover>
        <template v-else>
          <i-button
            class="tw-tracking-wider"
            @click="isEmailActivationDialogOpen = true"
          >
            {{ $t('foreigners.basic_info.activate_button') }}
          </i-button>
          <EmailActivationDialog
            :is-open="isEmailActivationDialogOpen"
            :foreigner-id="foreigner.id"
            @close="isEmailActivationDialogOpen = false"
          />
        </template>
      </template>

      <i-button
        is-icon
        @click="isNotificationsSettingsDialogOpen = true"
      >
        <icon-general-notification />
      </i-button>
      <NotificationsSettingsDialog
        :is-open="isNotificationsSettingsDialogOpen"
        @close="isNotificationsSettingsDialogOpen = false"
      />

      <i-confirm-popover
        v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
        :cancel-text="$t('common.actions.cancel')"
        :submit-text="$t('common.actions.delete')"
        @submit="deleteForeigner"
      >
        <template #reference>
          <i-button
            :is-loading="isDeleting"
            is-icon
          >
            <icon-trash class="tw-text-primary dark:tw-text-dm-primary" />
          </i-button>
        </template>
        <template #content>
          <div>
            {{ $t('foreigners.basic_info.delete_dialog.confirm_text') }}
            <span class="tw-whitespace-nowrap">
              {{ foreigner.first_name }} {{ foreigner.last_name }}?
            </span>
          </div>
        </template>
      </i-confirm-popover>
    </div>

    <div class="tw-flex md:tw-gap-10 tw-flex-wrap lg:tw-flex-nowrap">
      <div class="tw-w-full lg:tw-w-1/3">
        <h3>
          {{ $t('foreigners.basic_info.sections.personal') }}
        </h3>
        <div class="tw-mb-8">
          <i-info-list-item
            v-if="gender"
            :title="$t('common.fields.gender')"
            :value="gender.name"
          />
          <i-info-list-item
            :title="$t('common.fields.citizenship')"
            :value="citizenship"
          />
          <i-info-list-item
            v-if="foreigner.family.length > 0"
            :title="$t('foreigners.basic_info.family.head')"
            :value="(foreigner.profile.master) ? $t('common.values.yes') : $t('common.values.no')"
          />
        </div>

        <div
          v-if="foreigner.phone_primary || foreigner.email || foreigner.phone_secondary"
        >
          <h3>
            {{ $t('foreigners.basic_info.sections.contact') }}
          </h3>
          <div>
            <i-info-list-item
              v-if="foreigner.phone_primary"
              :title="$t('common.fields.phone')"
            >
              <template #value>
                <a
                  :href="`tel:${foreigner.phone_primary}`"
                >
                  {{ foreigner.phone_primary }}
                </a>
              </template>
            </i-info-list-item>
            <i-info-list-item
              v-if="foreigner.email"
              :title="$t('common.fields.email')"
            >
              <template #value>
                <a
                  :href="`mailto:${foreigner.email}`"
                >
                  {{ foreigner.email }}
                </a>
              </template>
            </i-info-list-item>
          </div>
        </div>
      </div>

      <div class="tw-w-full lg:tw-w-1/3">
        <h3>
          {{ $t('foreigners.basic_info.sections.client') }}
        </h3>
        <div class="tw-mb-8">
          <i-info-list-item
            :title="$t('common.fields.company')"
            :value="foreigner.profile.client_company"
          />
          <i-info-list-item
            :title="$t('foreigners.basic_info.fields.hire_origin')"
            :value="hireOrigin"
          />
          <i-info-list-item
            v-if="foreigner.profile.assigned_to_hr_id"
            :title="$t('foreigners.basic_info.assigned_hr')"
            :value="foreigner.profile.assigned_to_hr"
          />
          <i-info-list-item
            v-if="foreigner.profile.client_reference_number"
            :title="$t('foreigners.basic_info.fields.cli_ref_number')"
            :value="foreigner.profile.client_reference_number"
          />
          <i-info-list-item
            :title="$t('foreigners.basic_info.fields.ref_number')"
            :value="foreigner.profile.reference_number"
          />
          <i-info-list-item
            v-if="foreigner.profile.official_job_code"
            :title="$t('foreigners.basic_info.fields.off_job_code')"
            :value="foreigner.profile.official_job_code"
          />
          <i-info-list-item
            v-if="foreigner.profile.supervisor"
            :title="$t('foreigners.basic_info.fields.supervisor')"
            :value="foreigner.profile.supervisor"
          />
        </div>
      </div>

      <div
        v-if="foreigner.family.length !== 0"
        class="tw-w-full lg:tw-w-1/3"
      >
        <h3 class="tw-mb-3">
          {{ $t('foreigners.basic_info.family_dialog.title') }}
        </h3>
        <div class="tw-mb-8">
          <router-link
            v-for="person in foreigner.family"
            :key="person.relation_with_id"
            :to="`/foreigners/${person.relation_with_id}`"
            class="tw-flex tw-items-center tw-mb-4"
          >
            <i-user-avatar
              :avatar="person.avatar_url && person.avatar_url['128'] && `${$baseHost}/${person.avatar_url['128']}`"
              :fullname="person.relation_with_fullname"
              size="small"
              class="tw-mr-3"
            />
            <div>
              <div class="tw-text-primary dark:tw-text-dm-primary tw-leading-tight">
                {{ person.relation_with_fullname }}
              </div>
              <div class="tw-text-gray-dark dark:tw-text-dm-gray-dark tw-leading-tight">
                {{ person.relationship_type_name }}
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NotificationsSettingsDialog from '@/components/Dialogs/NotificationsSettingsDialog.vue';
import EmailActivationDialog from '@/components/Dialogs/EmailActivationDialog.vue';
import { mapState } from 'vuex';

export default {
  name: 'V2ForeignersBasicInfoView',
  components: {
    NotificationsSettingsDialog,
    EmailActivationDialog,
  },
  data() {
    return {
      isDeleting: false,
      isPasswordResetting: false,
      isNotificationsSettingsDialogOpen: false,
      isEmailActivationDialogOpen: false,
    };
  },
  computed: {
    ...mapState({
      foreigner: (state) => state.v2.admin.foreigners.foreigner.foreigner,
    }),
    gender() {
      return this.$store.state.v2.shared.enums.enums.genders.find((gender) => gender.id === this.foreigner?.gender);
    },
    citizenship() {
      const country = this.$store.getters['v2/shared/enums/countryById'](this.foreigner?.profile?.citizenship_id);
      return country && country.name || '';
    },
    hireOrigin() {
      const hireOrigin = this.$store.getters['v2/shared/enums/getHireOrigin'](this.foreigner?.profile.hire_origin);
      return hireOrigin && hireOrigin.name || '';
    },
  },

  methods: {
    async deleteForeigner() {
      this.isDeleting = true;

      try {
        await this.$store.dispatch('v2/admin/foreigners/foreigner/deleteForeigner', this.foreigner?.id);
        this.$router.push('/foreigners');
      } catch {
        this.$message({
          showClose: true,
          message: 'Error',
          type: 'error',
        });
      }

      this.isDeleting = false;
    },

    async resetPassword() {
      this.isPasswordResetting = true;

      try {
        const response = await this.$store.dispatch('v2/admin/foreigners/foreigner/resetPassword', { foreignerId: this.foreigner?.id });

        this.$message({
          showClose: true,
          message: this.$capitalize(response.data.message),
          type: 'success',
        });
      } catch (error) {
        console.error(error);

        this.$message({
          showClose: true,
          message: 'Error',
          type: 'error',
        });
      }


      this.isPasswordResetting = false;
    },
  },
};
</script>
